import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, ChevronDown, ChevronUp, User, LogIn, UserPlus, LogOut } from 'lucide-react';
import type { NavItem } from '@/types';
import { ROUTES } from '@/config/routes';
import { useAppDispatch, useAppSelector } from '@/store';
import { uiActions } from '@/store/slices/uiSlice';
import { authActions } from '@/store/slices/authSlice';
import { authService } from '@/services/api/auth.service';
import { generateId } from '@/utils/helpers';

interface MobileMenuProps {
  items: NavItem[];
}

export function MobileMenu({ items }: MobileMenuProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const close = () => dispatch(uiActions.setMobileMenuOpen(false));

  const handleLogout = async () => {
    await authService.logout();
    dispatch(authActions.logout());
    dispatch(uiActions.addToast({ id: generateId(), message: 'Logged out successfully.', type: 'success' }));
    close();
    navigate(ROUTES.HOME);
  };

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={close} />

      {/* Panel */}
      <div className="absolute inset-y-0 left-0 w-80 max-w-[85vw] bg-white shadow-lg">
        <div className="flex h-16 items-center justify-between border-b border-border px-4">
          <span className="text-lg font-bold text-text-primary">
            Beauty<span className="text-accent"> Sports</span>
          </span>
          <button
            onClick={close}
            className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)] hover:bg-bg-secondary"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 64px)' }}>
          <ul className="space-y-1">
            {items.map((item) => (
              <MobileNavItem key={item.label} item={item} onClose={close} />
            ))}
          </ul>

          <div className="mt-6 border-t border-border pt-4">
            {isAuthenticated ? (
              <div className="space-y-2">
                <Link
                  to={ROUTES.PROFILE}
                  onClick={close}
                  className="flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-bg-secondary"
                >
                  <User className="h-4 w-4" />
                  My Account
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-bg-secondary hover:text-error"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to={ROUTES.LOGIN}
                  onClick={close}
                  className="flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-bg-secondary"
                >
                  <LogIn className="h-4 w-4" />
                  Login
                </Link>
                <Link
                  to={ROUTES.REGISTER}
                  onClick={close}
                  className="flex items-center gap-3 rounded-[var(--radius-sm)] bg-accent px-3 py-2.5 text-sm font-medium text-white hover:bg-accent-hover"
                >
                  <UserPlus className="h-4 w-4" />
                  Register
                </Link>
              </div>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
}

interface MobileNavItemProps {
  item: NavItem;
  onClose: () => void;
}

function MobileNavItem({ item, onClose }: MobileNavItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  if (!hasChildren) {
    return (
      <li>
        <Link
          to={item.href}
          onClick={onClose}
          className="block rounded-[var(--radius-sm)] px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-bg-secondary hover:text-text-primary"
        >
          {item.label}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-[var(--radius-sm)] px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-bg-secondary"
      >
        {item.label}
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && (
        <ul className="ml-4 mt-1 space-y-1 border-l border-border-light pl-3">
          {item.children?.map((child) => (
            <li key={child.label}>
              <Link
                to={child.href}
                onClick={onClose}
                className="block rounded-[var(--radius-sm)] px-3 py-2 text-sm text-text-muted hover:text-accent"
              >
                {child.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
